/**
 * Variable interpolation utilities for the Flow Engine
 * Handles {{variable}} replacement with support for React patterns
 */

import type { VariableResolverOptions } from './types';

/**
 * Utility class for variable interpolation in flow content
 * 
 * Supports:
 * - Simple variable replacement: {{variableName}}
 * - Nested object traversal: {{user.name}}
 * - Default values for missing variables
 * - Custom transformers for special formatting
 * 
 * @example
 * ```typescript
 * const variables = { userName: 'John', count: 5 };
 * const result = VariableResolver.interpolate('Hello {{userName}}! You have {{count}} messages.', variables);
 * // Result: "Hello John! You have 5 messages."
 * ```
 */
export class VariableResolver {
  private static defaultOptions: Required<VariableResolverOptions> = {
    defaultValue: '',
    throwOnMissing: false,
    transformers: {},
  };

  /**
   * Interpolate variables in a template string
   * Replaces {{variable}} with the value from the variables object
   * 
   * @param template - String containing variable placeholders
   * @param variables - Object containing variable values
   * @param options - Configuration options for interpolation
   * @returns String with variables replaced
   */
  static interpolate(
    template: string,
    variables: Record<string, any>,
    options: Partial<VariableResolverOptions> = {}
  ): string {
    if (!template) return template;

    const config = { ...this.defaultOptions, ...options };

    return template.replace(/\{\{(\w+(?:\.\w+)*)\}\}/g, (match, varPath) => {
      const value = this.getNestedValue(variables, varPath);
      
      if (value === undefined) {
        if (config.throwOnMissing) {
          throw new Error(`Variable not found: ${varPath}`);
        }
        return config.defaultValue;
      }

      // Apply custom transformer if available
      const transformer = config.transformers[varPath];
      if (transformer) {
        return transformer(value);
      }

      return this.formatValue(value);
    });
  }

  /**
   * Interpolate variables in all string properties of an object
   * Recursively processes nested objects and arrays
   * 
   * @param obj - Object to process
   * @param variables - Variables for interpolation
   * @param options - Configuration options
   * @returns Object with interpolated string values
   */
  static interpolateObject<T>(
    obj: T,
    variables: Record<string, any>,
    options: Partial<VariableResolverOptions> = {}
  ): T {
    if (obj === null || obj === undefined) return obj;

    if (typeof obj === 'string') {
      return this.interpolate(obj, variables, options) as T;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.interpolateObject(item, variables, options)) as T;
    }

    if (typeof obj === 'object') {
      const result: any = {};
      for (const [key, value] of Object.entries(obj)) {
        result[key] = this.interpolateObject(value, variables, options);
      }
      return result as T;
    }

    return obj;
  }

  /**
   * Check if a string contains variable placeholders
   * 
   * @param template - String to check
   * @returns True if template contains variables
   */
  static hasVariables(template: string): boolean {
    if (!template || typeof template !== 'string') return false;
    return /\{\{(\w+(?:\.\w+)*)\}\}/.test(template);
  }

  /**
   * Extract variable names from a template string
   * 
   * @param template - String to analyze
   * @returns Array of variable names found
   */
  static extractVariableNames(template: string): string[] {
    if (!template || typeof template !== 'string') return [];
    
    const matches = template.matchAll(/\{\{(\w+(?:\.\w+)*)\}\}/g);
    return Array.from(matches, (m) => m[1]);
  }

  /**
   * Find variable names that are missing from the provided variables
   * 
   * @param template - Template string to check
   * @param variables - Available variables object
   * @returns Array of missing variable names
   */
  static findMissingVariables(template: string, variables: Record<string, any>): string[] {
    const required = this.extractVariableNames(template);
    const missing: string[] = [];

    for (const varPath of required) {
      const value = this.getNestedValue(variables, varPath);
      if (value === undefined || value === null) {
        missing.push(varPath);
      }
    }

    return missing;
  }

  /**
   * Create a safe copy of variables for interpolation
   * Removes functions and circular references
   * 
   * @param variables - Original variables object
   * @returns Sanitized variables object
   */
  static sanitizeVariables(variables: Record<string, any>): Record<string, any> {
    const seen = new WeakSet();
    
    const sanitize = (obj: any): any => {
      if (obj === null || typeof obj !== 'object') {
        return typeof obj === 'function' ? '[Function]' : obj;
      }

      if (seen.has(obj)) {
        return '[Circular]';
      }
      seen.add(obj);

      if (Array.isArray(obj)) {
        return obj.map(sanitize);
      }

      const result: any = {};
      for (const [key, value] of Object.entries(obj)) {
        result[key] = sanitize(value);
      }
      return result;
    };

    return sanitize(variables);
  }

  /**
   * Get nested property value using dot notation
   * 
   * @private
   * @param obj - Object to traverse
   * @param path - Dot-separated path (e.g., 'user.profile.name')
   * @returns Value at path or undefined
   */
  private static getNestedValue(obj: Record<string, any>, path: string): any {
    return path.split('.').reduce((current, key) => {
      return current?.[key];
    }, obj);
  }

  /**
   * Format a value for string interpolation
   * 
   * @private
   * @param value - Value to format
   * @returns String representation
   */
  private static formatValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return '[Object]';
      }
    }
    
    return String(value);
  }
}